import { DEFAULT_TONIC, TONICS, Tonic } from "../model";
import {
    ENCODED_SWARA_LENGTH,
    IAcceptableKeyInputs,
    IToggleableKeyInputs,
    encodeSwara,
} from "../tracks/sargam";

/*---------------------------------------------------------------------------
  Sargam encoding -> absolute pitch.

  This module knows two things and nothing else: how to take a stored value
  apart into 4-character swaras, and what MIDI note each one is. It holds no
  timing and no MIDI file knowledge.

  Sa is a MOVABLE tonic, so "what note is this" is not answerable from the
  document alone — it needs `fileOptions.scaleTonic`. Every function here takes
  the tonic explicitly rather than reaching for a default, which is the same
  discipline `blankRow(taalCount, vectorLength)` follows: no ambient state, so
  the whole module runs on a server and in a test unchanged.
---------------------------------------------------------------------------*/

/** Character positions inside one encoded swara. See `sargam.ts`. */
const KAN_INDEX = 1;
const SAPTAK_INDEX = 2;
const SWAR_INDEX = 3;

/** 1 madhya, 2 mandra, 3 taar. */
export type Saptak = 1 | 2 | 3;

const bareCodeFor = (key: IAcceptableKeyInputs): string =>
    encodeSwara({ key, mandra: false, taar: false, kanSwar: false }).charAt(
        SWAR_INDEX
    );

/**
 * The twelve swaras in ascending pitch order.
 *
 * THIS LIST IS THE ONLY THING WRITTEN OUT BY HAND HERE, and it is a fact about
 * the instrument rather than about the encoding: a harmonium octave is twelve
 * keys and this is the order they sit in. The swar CODE for each one is asked
 * of `encodeSwara`, so if the codec ever reassigns a code the table follows
 * automatically.
 *
 * The alternative — writing `semitone = parseInt(code, 16) - 1`, which happens
 * to hold today — relies on a coincidence between two independently maintained
 * lists. The day someone inserts a code, that formula transposes every export
 * by a semitone with no error anywhere.
 */
const CHROMATIC_KEYS: readonly IToggleableKeyInputs[] = [
    "s",
    "r",
    "R",
    "g",
    "G",
    "m",
    "M",
    "p",
    "d",
    "D",
    "n",
    "N",
];

/**
 * Swar code -> semitones above Sa.
 *
 * Frozen because this module evaluates on the server, where a module-level
 * object is shared by every concurrent request.
 */
export const CODE_TO_SEMITONE: Readonly<Record<string, number>> = Object.freeze(
    CHROMATIC_KEYS.reduce<Record<string, number>>((table, key, semitone) => {
        table[bareCodeFor(key)] = semitone;
        return table;
    }, {})
);

/**
 * The swar code the space bar types: a rest.
 *
 * Derived rather than retyped, for the same reason as the semitone table. The
 * codec keeps this value private, so asking `encodeSwara` is the only way to
 * read it without duplicating it.
 */
export const REST_SWAR_CODE = bareCodeFor(" ");

/**
 * Which MIDI note madhya Sa lands on when Sa is tuned to C.
 *
 * 60 is middle C, so the twelve tonics occupy 60–71 and the whole composition
 * sits in the octave a singer and a piano roll both expect. The extremes are
 * mandra Sa at Sa = C (48) and taar Ni at Sa = B (94) — comfortably inside
 * 0–127 for every tonic, which is why nothing here clamps. A clamp would
 * silently transpose a note rather than fail, and there is no case for it to
 * catch.
 */
export const MADHYA_SA_MIDI_NOTE = 60;

const SAPTAK_OCTAVE_SHIFT: Record<Saptak, number> = { 1: 0, 2: -12, 3: 12 };

/** The MIDI note madhya Sa lands on for this tonic. */
export function tonicMidiNote(tonic: Tonic): number {
    const index = TONICS.indexOf(tonic);
    // An unrecognised tonic would otherwise rotate off the end of the table and
    // transpose the file down a semitone. Fall back rather than render nonsense,
    // matching `noteNamesForTonic`.
    return MADHYA_SA_MIDI_NOTE + (index === -1 ? TONICS.indexOf(DEFAULT_TONIC) : index);
}

/**
 * A swar code and octave -> MIDI note number, or `null` when the code carries
 * no pitch (a rest, or a code this build does not know).
 */
export function midiNoteFor(
    code: string,
    saptak: Saptak,
    tonic: Tonic
): number | null {
    const semitone = CODE_TO_SEMITONE[code];
    if (semitone === undefined) return null;
    return tonicMidiNote(tonic) + semitone + SAPTAK_OCTAVE_SHIFT[saptak];
}

export interface DecodedSwara {
    /** A kan swara: `countSoundedNotes` excludes it, so it gets no equal share. */
    kan: boolean;
    saptak: Saptak;
    /** The raw swar hex, or `REST_SWAR_CODE`. */
    code: string;
    /** True when the octave character was not 1, 2 or 3; `saptak` reads madhya. */
    unknownSaptak: boolean;
}

export function decodeSwara(chunk: string): DecodedSwara {
    const raw = chunk.charAt(SAPTAK_INDEX);
    const known = raw === "1" || raw === "2" || raw === "3";
    return {
        kan: chunk.charAt(KAN_INDEX) === "1",
        // Madhya on an unreadable octave: the pitch class is still known, and
        // silencing the note would lose more than guessing the register does.
        saptak: known ? (Number(raw) as Saptak) : 1,
        code: chunk.charAt(SWAR_INDEX),
        unknownSaptak: !known,
    };
}

/**
 * A stored value -> its swaras, or `null` when the value is malformed.
 *
 * `null` rather than a sentinel string so the caller cannot forget to check:
 * the editor renders `!!` for this case and must not crash, and neither may
 * anything downstream of here.
 */
export function splitSwaras(value: string): string[] | null {
    if (value.length % ENCODED_SWARA_LENGTH !== 0) return null;
    const chunks: string[] = [];
    for (let i = 0; i < value.length; i += ENCODED_SWARA_LENGTH) {
        chunks.push(value.slice(i, i + ENCODED_SWARA_LENGTH));
    }
    return chunks;
}
